
import { useEffect, useState } from 'react';
import { useSubdomain } from '@/hooks/use-subdomain';
import { useNavigate, useLocation } from 'react-router-dom';
import { CSOLoadingSpinner } from '@/components/ui/cso-loading-spinner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';

export function SubdomainRouter({ children }: { children: React.ReactNode }) {
  const { subdomain, organization, isLoading, isMainDomain } = useSubdomain();
  const navigate = useNavigate();
  const location = useLocation();
  const [routingComplete, setRoutingComplete] = useState(false);

  useEffect(() => {
    const handleSubdomainRouting = async () => {
      if (isLoading) return;

      console.log('SubdomainRouter: Checking routing', { 
        isMainDomain, 
        subdomain, 
        organization: !!organization,
        hostname: window.location.hostname 
      });

      // If on main domain or Lovable preview, proceed normally without any routing logic
      if (isMainDomain) {
        console.log('SubdomainRouter: On main domain, allowing normal routing');
        setRoutingComplete(true);
        return;
      }

      // Only apply subdomain logic if we're actually on a real subdomain
      if (subdomain && !organization) {
        console.log('Subdomain not found:', subdomain);
        // Only redirect if we're actually on a csoagent.com subdomain
        if (window.location.hostname.includes('csoagent.com')) {
          window.location.href = `https://csoagent.com/?error=subdomain-not-found`;
          return;
        }
      }

      // If organization found, apply white-label routing logic
      if (organization) {
        console.log('Organization found for subdomain:', organization);
        
        // Check if organization has white-label enabled
        if (!organization.branding?.enableWhiteLabel) {
          console.log('White-label not enabled for organization');
          if (window.location.hostname.includes('csoagent.com')) {
            window.location.href = `https://csoagent.com/?error=white-label-disabled`;
            return;
          }
        }

        // Apply subdomain-specific routing
        const currentPath = location.pathname;
        
        // Redirect certain paths for subdomain users
        if (currentPath === '/auth') {
          // Allow auth on subdomains for team member signup
        } else if (currentPath.startsWith('/organization')) {
          // Redirect org management to main domain
          if (window.location.hostname.includes('csoagent.com')) {
            window.location.href = `https://csoagent.com${currentPath}`;
            return;
          }
        }
      }

      setRoutingComplete(true);
    };

    handleSubdomainRouting();
  }, [subdomain, organization, isLoading, isMainDomain, location.pathname]);

  // Always show content immediately for main domain (including Lovable)
  if (isMainDomain) {
    return <>{children}</>;
  }

  // For real subdomains, show loading state while routing
  if (isLoading || !routingComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <CSOLoadingSpinner size="lg" />
      </div>
    );
  }

  // Show error for invalid subdomain (only on real csoagent.com subdomains)
  if (!isMainDomain && !organization && window.location.hostname.includes('csoagent.com')) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Alert className="max-w-md">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            This subdomain is not configured. Please check the URL or contact support.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return <>{children}</>;
}
